import React, { useState, useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { useQuestionnaire } from 'hooks/useQuestionnaire';
import { useTabContext } from 'context/TabContext';
import { Box, Button, Fade } from '@chakra-ui/react';
import Heading from 'components/Heading/Heading';
import Checkbox from 'components/Checkbox/Checkbox';
import SectionComponent from 'components/SectionComponent/SectionComponent';
import { useLoading } from 'hooks/useLoading';
import { useSession } from 'context/SessionContext';
import { Section } from 'types';
import { useUserSession } from 'context/UserContext';
import { initializeDefaultValues, resetFormValues } from 'utils/formUtils';
import { arraysEqual } from 'utils/arrayUtils';
import { useExtraQuestionsData } from 'context/ExtraQuestionsContext';
import { fetchQuestions as handleFetchQuestions, fetchExtraQuestions } from 'hooks/useFetchQuestions';

const Base: React.FC = () => {
  const currentTab = 1;
  const { completeTab, activeTab, loading, setLoading, enabledTabs } = useTabContext();
  const { startLoading, stopLoading, LoadingSpinner } = useLoading();
  const [baseData, setBaseData] = useState<Section[]>([]);
  const [needsRecalculate, setNeedsRecalculate] = useState(false);
  const [extraData, setExtraData] = useState<Section[]>([]);
  const { saveQuestionnaire, data } = useSession();
  const { setExtraAnswers, extraAnswers } = useExtraQuestionsData();

  const {
    validateStep,
    validateInputs,
    validateSubQuestion,
    editValues,
    transformValuesToExtraAnswers,
    mapQuestions,
  } = useQuestionnaire();

  const {
    control,
    handleSubmit,
    getValues,
    reset,
    setValue,
    formState: { errors }
  } = useForm() 
  
  const { userData, getDynamicQuestionnaire } = useUserSession();
  const questionnaireCode = useMemo(() => getDynamicQuestionnaire('BAS')?.value ?? '', [userData])

  useEffect(() => {
    if (activeTab === currentTab && baseData.length === 0) {
      const fetchQuestions = async () => {
        startLoading();
        try {
        
          const { sections } = await handleFetchQuestions(questionnaireCode, 'A');

          setBaseData(sections);
        } catch (error: any) {
        } finally {
          stopLoading();
        }
      };

      fetchQuestions();
    }
  }, [activeTab]);

  useEffect(() => {
    if (!enabledTabs.includes(currentTab)) {
      handleReset();
    }
  }, [enabledTabs]);

  const handleReset = () => {
    setNeedsRecalculate(false);
    setExtraData([]);
    resetFormValues(baseData, setValue, reset, initializeDefaultValues(baseData));
  };

  useEffect(() => {
    cleanExtraAnswers();
    if (needsRecalculate) {
      setExtraData([])
    }
  }, [needsRecalculate])
  
  const findCountryNode = (items: any) => {
    for (const item of items) {
      if (item.lovType === "COUNTRY") {
        return item;
      }
      
      if (Array.isArray(item.items) && item.items.length > 0) {
        const result: any = findCountryNode(item.items);
        if (result) {
          return result;
        }
      }
    }
    return null;
  }

  const getSelectedCountries = (questions: any) => {
    if (!baseData?.[0]?.items) return [];
    const countryLov = getLov("COUNTRY");
    return countryLov ? questions[countryLov.value] || [] : [];
  };

  const getLov = (lovCode: string) => {
    return baseData[0].items
    .flatMap(item => item.items || [])
    .find(({ lovType }) => lovType === lovCode);
  }

  const validateBaseQuestions = async () => {
    const questions = getValues();
    const countryNode = findCountryNode(baseData[0].items);
    const countries = countryNode?.items ?? [];
    const selectedCountries = getSelectedCountries(questions);

    const isRiskCountry = selectedCountries.some((selectedCode: any) => {
      const country = countries.find((n: any) => n.code === selectedCode);
      return country?.high === 'Y';
    });

    sessionStorage.setItem('isRiskCountry', JSON.stringify(isRiskCountry));
    const sportLov = getLov("DESP");
    const activityLov = getLov("ATIV");
    const activityCodeMap = {
      ...(sportLov?.value && { [sportLov.value]: 'S' }),
      ...(activityLov?.value && { [activityLov.value]: 'A' }),
    };
    const transformedData = transformValuesToExtraAnswers(questions, activityCodeMap);

    if (!needsRecalculate) {
      const showResidenceTab = selectedCountries.some((selectedCode: any) => {
        const country = countries.find((n: any) => n.code === selectedCode);
        return country?.classCode?.substring(0,5) === "Quest";
      });

      handleSubmitBase(showResidenceTab);
    } else {
      setLoading(true);

      const getExtraQuestionsRequest = transformedData.filter(d => d.activity !== 'P');
      
      if (getExtraQuestionsRequest.length === 0) {
        setNeedsRecalculate(false);
        setLoading(false);
        return;
      }

      try {
       
        const { result } = await fetchExtraQuestions(getExtraQuestionsRequest);

        setNeedsRecalculate(false);
        setExtraData(result.sections);
      } catch (ex) {
        console.error('Error ao enviar dados:', ex);
      } finally {
        setLoading(false);
      }
    }
  };

  const handleSubmitBase = async (showResidenceTab : boolean) => {
    setLoading(true);

    const baseValues = getValues();
    validateStep(baseValues);
    const answersBase = editValues(getValues);

    const mappedQuestions = mapQuestions(extraData, baseValues);

    const answerBaseData = {
      questionnaireId: questionnaireCode,
      activityCode: 'A',
      answers: [...answersBase],
      showResidenceTab : showResidenceTab
    };

    saveQuestionnaire(answerBaseData, data.extraQuestions ? [...data.extraQuestions,...mappedQuestions] : mappedQuestions);

    try {
      completeTab(activeTab);
    } catch (error) {
      console.error('Error ao enviar dados:', error);
    } finally {
      setLoading(false);
    }
  };
  
  const handleFieldChange = ( nextTargetKeys: string[], direction: any, moveKeys: string[], questionCode: string) => {
    const formValues = getValues();
    const sportLov = getLov("DESP");
    const activityLov = getLov("ATIV");

    let hasDependencyChanges = false;
    if(direction === 'left') {
      

      //if some sports dependency exists, remove dependent values
      if(sportLov?.dependency === questionCode){
        
        let toRemove = sportLov.items.filter((p: any) => p.parentCode.includes(moveKeys)).map((c: any) => c.code);

        let stayedItems = formValues[sportLov.value].filter((x: any) => !toRemove.includes(x));

        hasDependencyChanges = hasDependencyChanges || !arraysEqual(stayedItems, formValues[sportLov.value]);      
        setValue(sportLov.value, stayedItems);
      }

      //if some activities dependency exists, remove dependent values
      if(activityLov?.dependency === questionCode){
        
        let toRemove = activityLov.items.filter((p: any) => p.parentCode.includes(moveKeys)).map((c: any) => c.code);

        let stayedItems = formValues[activityLov.value].filter((x: any) => !toRemove.includes(x));

        hasDependencyChanges = hasDependencyChanges || !arraysEqual(stayedItems, formValues[activityLov.value]);      
        setValue(activityLov.value, stayedItems);
      }
    }

    if (questionCode !== sportLov?.value && questionCode !== activityLov?.value && !hasDependencyChanges) return;

    if (!arraysEqual(nextTargetKeys, formValues[questionCode]) || hasDependencyChanges) {
      setNeedsRecalculate(true);
    }
  }

  const getQuestionByLov = (lovType: string) => {
    return baseData[0].items.find((item: any) => {
      return item.items?.some((i: any) => i.lovType === lovType);
    });
  }

  const cleanExtraAnswers = () => {
    setExtraAnswers(extraAnswers.filter(e => e.activity !== 'A' && e.activity !== 'S'))
  }

  const checkForExtraQuestions = () => {
    const formValues = getValues();
    const sportQuestion = getQuestionByLov('DESP');
    const activityQuestion = getQuestionByLov('ATIV');

    if (!sportQuestion || !activityQuestion) return;

    if (formValues[sportQuestion.value] === "true" || formValues[activityQuestion.value] === "true") {
      setNeedsRecalculate(true);
      return;
    }

    cleanExtraAnswers();
    setExtraData([]);
    setNeedsRecalculate(false);
  }

  return (
    <Box>
      <Box>
        <Heading title="Base" />
      </Box>

      <Box>
        <form onSubmit={handleSubmit(validateBaseQuestions)}>
          {baseData.map(sectionList => (
            <SectionComponent
              key={sectionList.code}
              sectionList={sectionList}
              control={control}
              validateInputs={validateInputs}
              getValues={getValues}
              validateSubQuestion={(response: boolean, items: any, array: any) => {
                if (items.some((d: any) => d.lovType === 'DESP' || d.lovType === 'ATIV')) {
                  checkForExtraQuestions();
                }
                validateSubQuestion(response, items, array);
              }}
              onChange={handleFieldChange}
              errors={errors}
              handleDataChange={setBaseData}
              dataListChange={baseData}
            />
          ))}

          {needsRecalculate && (
            <>
              <Checkbox
                name="baseConfirmInfo"
                control={control}
                label="Confirmo a Informação"
                rules={{ required: 'Campo de preenchimento obrigatório' }}
                error={errors?.['baseConfirmInfo']}
              />

              <Box display="flex" alignItems="center" justifyContent="flex-end">
                <Button
                  variant="primary"
                  type="submit"
                  isLoading={loading}
                  loadingText="A Recalcular"
                >
                  Recalcular
                </Button>
              </Box>
            </>
          )}

          {!needsRecalculate && (
            <Fade in={!needsRecalculate}>
              <Box>
                {extraData.map(extraDataList => (
                  <SectionComponent
                    key="extraDataList"
                    sectionList={extraDataList}
                    control={control}
                    validateInputs={validateInputs}
                    getValues={getValues}
                    validateSubQuestion={validateSubQuestion}
                    onChange={handleFieldChange}
                    errors={errors}
                    handleDataChange={setExtraData}
                    dataListChange={extraData}
                  />
                ))}
                <Checkbox
                  name="baseConfirmInfo"
                  control={control}
                  label="Confirmo a Informação"
                  rules={{ required: 'Campo de preenchimento obrigatório' }}
                  error={errors?.['baseConfirmInfo']}
                />

                <Box display="flex" alignItems="center" justifyContent="flex-end">
                  <Button
                    variant="primary"
                    type="submit"
                    isLoading={loading}
                    loadingText="A Enviar"
                  >
                    Continuar
                  </Button>
                </Box>
              </Box>
            </Fade>
          )}
        </form>
      </Box>
      <LoadingSpinner />
    </Box>
  );
};

export default Base;
